<template>
  <v-dialog v-model="showDialog" max-width="600px">
    <v-card>
      <v-card-title class="text-h5"> {{ t('model.connection') }} </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <v-container>
            <v-row>
              <v-select
                :label="t('model.area')"
                :items="modelBereich"
                v-model="modelConnectionParameter.sectionId"
                :item-title="'description'"
                :item-value="'id'"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :disabled="!isNew"
                @update:model-value="sectionChanged()"
              ></v-select>
            </v-row>
            <v-row>
              <v-text-field
                v-model="modelConnectionParameter.description"
                :label="t('model.description')"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.description')"
                variant="outlined"
              ></v-text-field>
            </v-row>
            <v-row>
              <v-text-field
                v-model="modelConnectionParameter.url"
                label="URL"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.url')"
                prepend-inner-icon="mdi-web"
              ></v-text-field>
            </v-row>
            <v-row v-if="modelConnectionParameter?.sectionId === 6">
              <v-text-field
                v-model="modelConnectionParameter.port"
                :label="t('model.port')"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.port')"
                type="number"
              >
              </v-text-field>
            </v-row>
            <v-row v-if="modelConnectionParameter?.connectionTypeId === 1">
              <v-text-field
                v-model="modelConnectionParameter.addionalParameter"
                :label="t('model.database')"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.database')"
                prepend-inner-icon="mdi-database-outline"
              ></v-text-field>
            </v-row>
            <v-row>
              <v-select
                label="Terminalsystem"
                :items="availableTerminalSystems"
                v-model="modelConnectionParameter.terminalsystemId"
                item-title="description"
                item-value="id"
                variant="outlined"
                density="compact"
                :placeholder="t('message.placeholder.terminalsystemOptional')"
                clearable
                :disabled="isTerminalSystemDisabled"
              ></v-select>
            </v-row>
            <v-row>
              <v-divider class="mb-6"></v-divider>
            </v-row>
            <v-row v-if="isUserRegistration">
              <v-text-field
                v-model="modelConnectionParameter.user"
                :label="t('model.userOrClient')"
                density="compact"
                :placeholder="t('message.placeholder.benutzer')"
                prepend-inner-icon="mdi-account-outline"
                variant="outlined"
              ></v-text-field>
            </v-row>
            <v-row v-if="isUserRegistration">
              <v-text-field
                v-model="modelConnectionParameter.passwort"
                :label="t('model.passwordOrSecret')"
                :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
                :type="visible ? 'text' : 'password'"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.password')"
                prepend-inner-icon="mdi-lock-outline"
                variant="outlined"
                @click:append-inner="visible = !visible"
              ></v-text-field>
            </v-row>

            <v-row v-if="modelConnectionParameter?.sectionId === 6">
              <v-text-field
                v-model="modelConnectionParameter.virtualhost"
                :label="t('model.VirtualHost')"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.VirtualHost')"
              >
              </v-text-field>
            </v-row>
            <v-row v-if="modelConnectionParameter?.sectionId === 6">
              <v-text-field
                v-model="modelConnectionParameter.uuid"
                :label="t('model.BcomUuid')"
                variant="outlined"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.BcomUuid')"
              >
              </v-text-field>
            </v-row>
            <v-row v-if="!isUserRegistration">
              <v-text-field
                v-model="modelConnectionParameter.apikey"
                :label="t('model.apikey')"
                :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
                :type="visible ? 'text' : 'password'"
                density="compact"
                :rules="[rules.required]"
                :placeholder="t('message.placeholder.password')"
                prepend-inner-icon="mdi-lock-outline"
                variant="outlined"
                @click:append-inner="visible = !visible"
              ></v-text-field>
            </v-row>
            <v-row>
              <DefaultConnectionCheck v-model:connectionCheckObject="connectionCheckObject" v-model:verbindungsparameter="modelConnectionParameter" />
            </v-row>
          </v-container>
        </v-form>
        <div>
          {{ t('message.info.passwordInfo') }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red-darken-1" variant="outlined" @click="close">
          {{ t('button.cancel') }}
        </v-btn>
        <v-btn color="green-darken-1" variant="outlined" @click="prepareSave">
          {{ t('button.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import DefaultConnectionCheck from '@/components/ConnectionTest/DefaultConnectionCheck.vue';
import ConnectionParameterApi from '@/apis/connectionparameter-api.js';
import { useI18n } from 'vue-i18n';
import useNotification from '@/hooks/useNotification';

const props = defineProps({
  verbindungsparameter: {
    type: Object,
    default: () => {},
  },
  sectionArray: {
    type: Array,
    default: () => [],
  },
  terminalSystemArray: {
    type: Array,
    default: () => [],
  },
  isNew: {
    type: Boolean,
    required: true,
  },
});

const { t } = useI18n();
const { setNotification } = useNotification();
const emit = defineEmits(['refresh']);

const modelTerminalSystem = ref(props.terminalSystemArray);
const form = ref();
const modelConnectionParameter = ref({});
const showDialog = defineModel('showDialog', { require: true });
const visible = ref(false);
const connectionCheckObject = ref({
  isConnectionSuccessful: false,
  isConnectionError: false,
});

const rules = {
  required: (value) => !!value || t('message.required'),
};

const availableTerminalSystems = computed(() => {
  if (!modelConnectionParameter?.value?.sectionId) return [];

  return modelTerminalSystem.value.filter((x) => x.sectionId == modelConnectionParameter.value.sectionId);
});

const modelBereich = computed(() => {
  if (props.isNew)
    return props.sectionArray.filter(
      (x) => x.sectionTypeId == 3 && (x.description == 'INTUS COM' || x.description == 'ISGUS' || x.description == 'dormakaba' || x.description == 'CTI'),
    );
  return props.sectionArray;
});

const isUserRegistration = computed(() => {
  if (!modelConnectionParameter?.value?.sectionId) return false;

  var section = props.sectionArray.find((x) => x.id == modelConnectionParameter.value.sectionId);
  if (!section) {
    return true;
  }
  if (section.label === 'Factorial') {
    modelConnectionParameter.value.apikey = modelConnectionParameter.value.passwort;
    return false;
  }

  return true;
});

const isTerminalSystemDisabled = computed(() => {
  if (!modelConnectionParameter?.value?.sectionId) return false;

  var section = props.sectionArray.find((x) => x.id == modelConnectionParameter.value.sectionId);
  return section?.sectionTypeId !== 3;
});

const close = () => {
  showDialog.value = false;
};

const prepareSave = async () => {
  const { valid } = await form.value.validate();

  const cpm = { ...modelConnectionParameter.value };

  if (cpm.sectionId === 6) {
    const dormaString = createDormaString(cpm);
    cpm.url = dormaString;
  }

  if (valid) {
    await save(cpm);
  } else {
    let message = {
      message: t('message.error.errorInput'),
      icon: 'mdi-alert-circle-outline',
      color: 'error',
    };
    setNotification(message);
  }
};

const save = async (connectionParameter) => {
  let response = {};
  if (props.verbindungsparameter.id > 0) {
    response = await ConnectionParameterApi.UpdateConnectionParameter(connectionParameter);
  } else {
    response = await ConnectionParameterApi.CreateConnectionParameter(connectionParameter);
  }

  if (response.status == 200 || response.status == 201) {
    let message = {
      message: t('message.success.save'),
      icon: 'mdi-check-circle-outline',
      color: 'success',
    };
    setNotification(message);
    emit('refresh');
    close();
  } else {
    let message = {
      message: t('message.error.errorSave'),
      icon: 'mdi-alert-circle-outline',
      color: 'error',
    };
    setNotification(message);
  }
};

const sectionChanged = () => {
  if (!modelConnectionParameter?.value) return;

  var section = props.sectionArray.find((x) => x.id === modelConnectionParameter.value.sectionId);
  if (!section) return;
  if (section.description === 'INTUS COM') modelConnectionParameter.value.connectionTypeId = 1;
  else modelConnectionParameter.value.connectionTypeId = 2;
};

const createDormaString = (cpm) => {
  return `user id=${cpm.user};host=${cpm.url};port=${cpm.port};virtual host=${cpm.virtualhost};uuid=${cpm.uuid}`;
};

onMounted(async () => {
  if (modelConnectionParameter?.value?.url === '') {
    connectionCheckObject.value.isConnectionSuccessful = false;
  }
});

watch(showDialog, (newVal) => {
  modelConnectionParameter.value = props.verbindungsparameter;
});
</script>
